import { z } from 'zod';

// Permission check result -------------------------------------------------
//
// Written by the bot's form_perm_check_listener (PR 4). The portal reads
// this to drive the channel-access panel - see
// .docs/feedback-forms/channel-access-ux.md for the full UX.

export const FormChannelPermSchema = z.object({
  channelId: z.string(),
  channelType: z.enum(['text', 'forum', 'announcement', 'thread', 'unknown']),
  isPrivate: z.boolean(),
  exists: z.boolean(),
  botIsMember: z.boolean(),
  canSend: z.boolean(),
  canEmbed: z.boolean(),
  canReadHistory: z.boolean(),
  canView: z.boolean(),
});
export type FormChannelPerm = z.infer<typeof FormChannelPermSchema>;

// Per-role outcome for the recruit pre-flight. Reasons map 1-1 to the
// guards inside the bot's recruit handler so the panel can render the
// same advice ("the bot's role needs to sit above this one in your
// Discord role list") without recomputing.
export const FormRecruitRoleFailureSchema = z.object({
  roleId: z.string(),
  reason: z.enum(['missing', 'managed', 'above-bot']),
});
export type FormRecruitRoleFailure = z.infer<typeof FormRecruitRoleFailureSchema>;

export const FormRecruitChannelFailureSchema = z.object({
  channelId: z.string(),
  reason: z.enum(['missing', 'no-manage']),
});
export type FormRecruitChannelFailure = z.infer<typeof FormRecruitChannelFailureSchema>;

// Recruit perm-check block. Always present on results emitted post-0043;
// nullable on the parent so legacy rows (perm-check ran before the
// recruit columns landed) parse cleanly. The Enable RPC treats a null
// block as "stale - recheck needed" iff the form has recruit configured.
export const FormRecruitPermSchema = z.object({
  // Echoed for symmetry with surveyChannel/responseChannel - lets the
  // panel render "no recruit configured" without re-reading the form
  // arrays.
  configured: z.boolean(),
  // Server-wide perms the bot lacks. Only populated when recruit roles
  // are configured (no point reporting missing Manage Roles for a
  // form that just grants channel access).
  missingGuildPerms: z.array(z.enum(['manage-roles'])),
  roleFailures: z.array(FormRecruitRoleFailureSchema),
  channelFailures: z.array(FormRecruitChannelFailureSchema),
});
export type FormRecruitPerm = z.infer<typeof FormRecruitPermSchema>;

export const FormPermCheckResultSchema = z.object({
  checkedAt: z.string().datetime({ offset: true }),
  surveyChannel: FormChannelPermSchema,
  responseChannel: FormChannelPermSchema,
  recruit: FormRecruitPermSchema.nullable().optional(),
});
export type FormPermCheckResult = z.infer<typeof FormPermCheckResultSchema>;
